import type { FC, ReactNode } from 'react'

import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MAX_QUANTITY } from '@/lib/forms'

interface QuantityStepperProps {
    value: number
    onChange: (value: number) => void
    label: string
    size?: 'md' | 'sm'
    disabled?: boolean
}

const QuantityStepper: FC<QuantityStepperProps> = ({ value, onChange, label, size = 'md', disabled = false }): ReactNode => {
    const button = cn(
        'inline-flex items-center justify-center text-bark transition-colors hover:bg-bark/6',
        'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-bark disabled:opacity-35 disabled:hover:bg-transparent',
        size === 'md' ? 'size-12' : 'size-10'
    )

    return (
        <div role='group' aria-label={label} className='inline-flex items-center rounded-[3px] border border-bark/25'>
            <button
                type='button'
                className={button}
                onClick={() => onChange(value - 1)}
                disabled={disabled || value <= 1}
                aria-label='Decrease quantity'
            >
                <Minus className='size-4' strokeWidth={1.75} />
            </button>
            <span aria-live='polite' className={cn('min-w-9 text-center font-heading tabular-nums text-bark', size === 'md' ? 'text-lg' : 'text-base')}>
                {value}
            </span>
            <button
                type='button'
                className={button}
                onClick={() => onChange(value + 1)}
                disabled={disabled || value >= MAX_QUANTITY}
                aria-label='Increase quantity'
            >
                <Plus className='size-4' strokeWidth={1.75} />
            </button>
        </div>
    )
}

export default QuantityStepper
